import React, { useState, useEffect } from 'react';
import { Drawer, Form, Input, Checkbox, message, Spin } from 'antd';
import { logInstance } from '../../AxiosConfig';
import './BookAppointmentModal.css';

const DAYS        = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DAYS_FULL   = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
const MONTHS_FULL = ['January','February','March','April','May','June',
                     'July','August','September','October','November','December'];

const padDate = (y, m, d) =>
    `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;

const fmtTime = (t) => {
    const [h, m] = t.split(':').map(Number);
    const ampm   = h < 12 ? 'AM' : 'PM';
    const h12    = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
};

const fmtSlot = (s) => `${fmtTime(s.start)} - ${fmtTime(s.end)}`;

const fullDateLabel = (dateStr) => {
    const d = new Date(dateStr + 'T00:00:00');
    return `${DAYS_FULL[d.getDay()]}, ${d.getDate()} ${MONTHS_FULL[d.getMonth()]} ${d.getFullYear()}`;
};

const BookAppointmentModal = ({ visible, onClose, userId, cardId, cardOwnerName, businessName, cardImage }) => {
    const [step, setStep]           = useState(1);
    const [form]                    = Form.useForm();
    const [guestInfo, setGuestInfo] = useState({});

    const today = new Date();
    const [calYear,  setCalYear]  = useState(today.getFullYear());
    const [calMonth, setCalMonth] = useState(today.getMonth());
    const [selDate,  setSelDate]  = useState(null);
    const [slots,    setSlots]    = useState([]);
    const [selSlot,  setSelSlot]  = useState(null); // full { start, end } object

    const [availDates,   setAvailDates]   = useState(null); // Set or null (null = all open)
    const [availLoading, setAvailLoading] = useState(false);
    const [slotsLoading, setSlotsLoading] = useState(false);
    const [submitting,   setSubmitting]   = useState(false);

    // Reset on close
    useEffect(() => {
        if (!visible) {
            setStep(1);
            setGuestInfo({});
            setSelDate(null);
            setSelSlot(null);  // reset full slot object
            setSlots([]);
            form.resetFields();
            setCalYear(today.getFullYear());
            setCalMonth(today.getMonth());
            setAvailDates(null);
        }
    }, [visible]);

    // Fetch available dates when modal opens
    useEffect(() => {
        if (!visible || !userId) return;
        setAvailLoading(true);
        logInstance.get(`/appointments/settings/${userId}/availability`)
            .then(res => {
                const groups  = res.data.availability || [];
                const dateSet = new Set();
                groups.forEach(g => {
                    if (!g.isUnavailable) g.dates.forEach(d => dateSet.add(d));
                });

                if (dateSet.size === 0) {
                    setAvailDates(null); // fallback: all future dates open
                } else {
                    setAvailDates(dateSet);
                    // Auto-select nearest available date
                    const todayStr = padDate(today.getFullYear(), today.getMonth(), today.getDate());
                    const nearest  = [...dateSet].sort().find(d => d >= todayStr);
                    if (nearest) {
                        const [y, m] = nearest.split('-').map(Number);
                        setCalYear(y);
                        setCalMonth(m - 1);
                        setSelDate(nearest);
                    }
                }
            })
            .catch(() => setAvailDates(null))
            .finally(() => setAvailLoading(false));
    }, [visible, userId]);

    // Fetch slots when date changes
    useEffect(() => {
        if (!selDate || !userId) return;
        setSlotsLoading(true);
        logInstance.get(`/appointments/slots?userId=${userId}&date=${selDate}`)
            .then(res => setSlots(res.data.slots || []))
            .catch(() => setSlots([]))
            .finally(() => setSlotsLoading(false));
        setSelSlot(null);
    }, [selDate]);

    const buildCells = () => {
        const firstDay    = new Date(calYear, calMonth, 1).getDay();
        const daysInMonth = new Date(calYear, calMonth + 1, 0).getDate();
        const cells = [];
        for (let i = 0; i < firstDay; i++) cells.push(null);
        for (let d = 1; d <= daysInMonth; d++) cells.push(d);
        return cells;
    };

    const isPastDay = (d) => {
        const cell      = new Date(calYear, calMonth, d);
        const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        return cell < todayOnly;
    };

    const isAvailable = (d) => {
        if (!d || isPastDay(d)) return false;
        if (availDates === null) return true;
        return availDates.has(padDate(calYear, calMonth, d));
    };

    const prevMonth = () => {
        if (calMonth === 0) { setCalYear(y => y - 1); setCalMonth(11); }
        else setCalMonth(m => m - 1);
    };

    const nextMonth = () => {
        if (calMonth === 11) { setCalYear(y => y + 1); setCalMonth(0); }
        else setCalMonth(m => m + 1);
    };

    const handleDateClick = (d) => {
        if (!isAvailable(d)) return;
        setSelDate(padDate(calYear, calMonth, d));
    };

    const handleFormSubmit = (values) => {
        setGuestInfo(values);
        setStep(3);
    };

    const handleConfirm = async () => {
        setSubmitting(true);
        try {
            await logInstance.post('/appointments', {
                userId,
                cardId,
                guestName:    guestInfo.fullName,
                guestEmail:   guestInfo.email,
                guestPhone:   guestInfo.phone   || '',
                guestCompany: guestInfo.company || '',
                date:         selDate,
                timeSlot:     selSlot?.start,
                consentGiven: true,
                purpose:      guestInfo.purpose || '',
            });
            setStep(4);
        } catch (err) {
            message.error(err?.response?.data?.message || 'Booking failed. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    const cells       = buildCells();
    const canContinue = selDate && selSlot;

    return (
        <Drawer
            open={visible}
            onClose={onClose}
            placement="bottom"
            height="90dvh"
            closable={false}
            className="book-appt-drawer"
            styles={{
                body:    { padding: 0, display: 'flex', flexDirection: 'column', height: '100%', minHeight: 300 },
                wrapper: { borderRadius: '20px 20px 0 0', overflow: 'hidden', maxWidth: 480, margin: '0 auto' },
            }}
        >
            {/* ── Fixed Header ── */}
            <div className="appt-header">
                <button
                    className="appt-hdr-btn"
                    onClick={() => step > 1 ? setStep(s => s - 1) : onClose()}
                >
                    ‹
                </button>
                <span className="appt-hdr-title">Book Appointment</span>
                <button className="appt-hdr-btn" onClick={onClose}>✕</button>
            </div>

            {/* ── Scrollable Body ── */}
            <div className="appt-body">
                <div className="appt-handle" />

                {/* ── Step 1: Calendar + Slots ── */}
                {step === 1 && (
                    <div className="appt-step">
                        <p className="appt-section-label">Select Date</p>

                        {availLoading ? (
                            <div className="appt-center-loader"><Spin /></div>
                        ) : (
                            <div className="appt-calendar">
                                <div className="cal-header">
                                    <button className="cal-nav" onClick={prevMonth}>‹</button>
                                    <span className="cal-month-title">{MONTHS_FULL[calMonth]} {calYear}</span>
                                    <button className="cal-nav" onClick={nextMonth}>›</button>
                                </div>
                                <div className="cal-grid">
                                    {DAYS.map(d => (
                                        <div key={d} className="cal-day-name">{d}</div>
                                    ))}
                                    {cells.map((d, i) => {
                                        const dateStr  = d ? padDate(calYear, calMonth, d) : null;
                                        const avail    = isAvailable(d);
                                        const selected = dateStr && selDate === dateStr;
                                        return (
                                            <div
                                                key={i}
                                                className={[
                                                    'cal-day',
                                                    !d       ? 'cal-empty'    : '',
                                                    d && !avail ? 'cal-disabled' : '',
                                                    d && avail  ? 'cal-available': '',
                                                    selected    ? 'cal-selected' : '',
                                                ].filter(Boolean).join(' ')}
                                                onClick={() => d && handleDateClick(d)}
                                            >
                                                {d || ''}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {selDate && (
                            <>
                                <div className="appt-sel-date">
                                    <span className="appt-sel-date-icon">📅</span>
                                    <span className="appt-sel-date-text">
                                        {fullDateLabel(selDate)}
                                        {!selSlot?.start && (
                                            <span className="appt-sel-date-hint"> — Select a time slot</span>
                                        )}
                                    </span>
                                </div>

                                <div className="appt-slots">
                                    {slotsLoading ? (
                                        <div className="appt-center-loader"><Spin size="small" /></div>
                                    ) : slots.length === 0 ? (
                                        <p className="slots-empty">No slots available for this date.</p>
                                    ) : (
                                        <div className="slots-grid">
                                            {slots.map(s => {
                                                const todayStr   = padDate(today.getFullYear(), today.getMonth(), today.getDate());
                                                const nowMins    = today.getHours() * 60 + today.getMinutes();
                                                const [h, m]     = s.start.split(':').map(Number);
                                                const isPastSlot = selDate === todayStr && (h * 60 + m) <= nowMins;
                                                const disabled   = !s.available || isPastSlot;
                                                return (
                                                    <button
                                                        key={s.start}
                                                        className={[
                                                            'slot-btn',
                                                            disabled                    ? 'slot-taken'  : '',
                                                            selSlot?.start === s.start  ? 'slot-active' : '',
                                                        ].filter(Boolean).join(' ')}
                                                        onClick={() => !disabled && setSelSlot(s)}
                                                        disabled={disabled}
                                                    >
                                                        {fmtSlot(s)}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>

                                <p className="appt-tz-note">⏰ All times are shown in India Standard Time (IST)</p>
                            </>
                        )}
                    </div>
                )}

                {/* ── Step 2: Enter Details ── */}
                {step === 2 && (
                    <div className="appt-step">
                        <p className="appt-section-label">Enter Details</p>
                        <p className="appt-subtitle">please provide your details to book an appointment.</p>

                        <Form form={form} layout="vertical" onFinish={handleFormSubmit} initialValues={guestInfo}>
                            <Form.Item
                                label="Full Name"
                                name="fullName"
                                rules={[{ required: true, message: 'Please enter your name' }]}
                            >
                                <Input placeholder="Enter your full name" size="large" className="appt-input" />
                            </Form.Item>

                            <Form.Item label="Company name" name="company">
                                <Input placeholder="Enter yourCompany Name" size="large" className="appt-input" />
                            </Form.Item>

                            <Form.Item
                                label="Email Address"
                                name="email"
                                rules={[{ required: true, type: 'email', message: 'Please enter a valid email' }]}
                            >
                                <Input placeholder="Enter your email address" size="large" className="appt-input" />
                            </Form.Item>

                            <Form.Item label="Phone Number (Optional)" name="phone">
                                <Input addonBefore="+91" placeholder="Enter your phone  number" size="large" className="appt-input" />
                            </Form.Item>

                            <Form.Item label="Purpose of Meeting" name="purpose">
                                <Input.TextArea
                                    placeholder="Type here."
                                    rows={3}
                                    className="appt-input"
                                />
                            </Form.Item>

                            <Form.Item
                                name="consent"
                                valuePropName="checked"
                                rules={[{
                                    validator: (_, v) =>
                                        v ? Promise.resolve() : Promise.reject(new Error('Please accept to continue')),
                                }]}
                            >
                                <Checkbox className="appt-consent">
                                    I agree to share my information with the host for this appointment.
                                </Checkbox>
                            </Form.Item>

                        </Form>
                    </div>
                )}

                {/* ── Step 3: Preview ── */}
                {step === 3 && (
                    <div className="appt-step">
                        <p className="appt-section-label">Enter Details</p>
                        <p className="appt-subtitle">please provide your details to book an appointment.</p>

                        <div className="preview-field">
                            <label className="preview-label">Full Name</label>
                            <div className="preview-value-box">{guestInfo.fullName}</div>
                        </div>

                        <div className="preview-field">
                            <label className="preview-label">Company name</label>
                            <div className="preview-value-box">{guestInfo.company || ''}</div>
                        </div>

                        <div className="preview-field">
                            <label className="preview-label">Email Address</label>
                            <div className="preview-value-box">{guestInfo.email}</div>
                        </div>

                        <div className="preview-field">
                            <label className="preview-label">Phone Number (Optional)</label>
                            <div className="preview-value-box preview-phone">
                                <span className="preview-phone-code">+91</span>
                                <span>{guestInfo.phone || ''}</span>
                            </div>
                        </div>

                        <div className="preview-field">
                            <label className="preview-label">Purpose of Meeting</label>
                            <div className="preview-value-box preview-textarea">{guestInfo.purpose || ''}</div>
                        </div>

                        <div className="preview-consent">
                            <Checkbox checked readOnly className="appt-consent">
                                I agree to share my information with the host for this appointment.
                            </Checkbox>
                        </div>
                    </div>
                )}

                {/* ── Step 4: Success overlay ── */}
                {step === 4 && (
                    <div className="booking-success-overlay">
                        <div className="booking-success-card">
                            <h3 className="booking-success-title">Booking Requested</h3>
                            <p className="booking-success-sub">
                                You are scheduled with {cardOwnerName}.
                            </p>

                            <div className="booking-success-biz">
                                {cardImage ? (
                                    <img src={cardImage} alt="" className="booking-biz-img" />
                                ) : (
                                    <div className="booking-biz-dot" />
                                )}
                                <span className="booking-biz-name">{businessName}</span>
                            </div>

                            <div className="booking-success-details">
                                <div className="booking-detail-row">
                                    <span className="booking-detail-icon">📅</span>
                                    <span>{fullDateLabel(selDate)}</span>
                                </div>
                                <div className="booking-detail-row">
                                    <span className="booking-detail-icon">🕐</span>
                                    <span>{fmtTime(selSlot?.start)} - {fmtTime(selSlot?.end)}</span>
                                </div>
                                {guestInfo.phone && (
                                    <div className="booking-detail-row">
                                        <span className="booking-detail-icon">📞</span>
                                        <span>+91{guestInfo.phone}</span>
                                    </div>
                                )}
                                <div className="booking-detail-row">
                                    <span className="booking-detail-icon">✉️</span>
                                    <span>{guestInfo.email}</span>
                                </div>
                            </div>

                            <p className="booking-success-note">
                                A calender invitation has been sent to your email address.
                            </p>
                        </div>
                    </div>
                )}
            </div>

            {/* ── Fixed Footer ── */}
            {step !== 4 && (
                <div className="appt-footer">
                    {step === 1 && (
                        <button
                            className="appt-btn-primary"
                            disabled={!canContinue}
                            onClick={() => setStep(2)}
                        >
                            Continue
                        </button>
                    )}
                    {step === 2 && (
                        <button
                            className="appt-btn-primary"
                            onClick={() => form.submit()}
                        >
                            Book Appointment
                        </button>
                    )}
                    {step === 3 && (
                        <button
                            className="appt-btn-primary"
                            onClick={handleConfirm}
                            disabled={submitting}
                        >
                            {submitting ? 'Booking...' : 'Book Appointment'}
                        </button>
                    )}
                </div>
            )}
        </Drawer>
    );
};

export default BookAppointmentModal;
